import numeral from 'numeral'
import { useMemo } from 'react'
import { IoInformationCircle } from 'react-icons/io5'

import { AreaDark } from 'src/components/atoms/Icons/contained/AreaDark'
import { CarbonDark } from 'src/components/atoms/Icons/contained/CarbonDark'
import { FundedDark } from 'src/components/atoms/Icons/contained/FundedDark'
import { SpadeDark } from 'src/components/atoms/Icons/contained/SpadeDark'
import { IconProps } from 'src/components/atoms/Icons/types'
import { ExternalLink, InternalLink } from 'src/components/atoms/Link'
import { Tooltip } from 'src/components/atoms/Tooltip'
import { Heading3 } from 'src/components/atoms/Typography'
import { calculateCarbon } from 'src/components/organisms/CarbonCalculator/helper'
import { CalculatorTab } from 'src/components/organisms/CarbonCalculator/types'
import { SwipeTiles } from 'src/components/organisms/forms/DonateForm/steps/Trees/DonateImpactTiles'
import { tileDescriptions } from 'src/components/organisms/forms/DonateForm/steps/Trees/helpers'

import { listFundingPath, supportedProjectsPath } from 'src/config/paths'

type ImpactSummaryProps = {
  treesFunded: number
  projectsSupported: number
  accountJoinYear: string
}

type SummaryItem = {
  stat: string
  statUnitText?: string
  text: string | JSX.Element
  icon: React.ComponentType<IconProps>
}

export const ImpactSummary = ({
  treesFunded,
  projectsSupported,
  accountJoinYear
}: ImpactSummaryProps) => {
  const carbonValue = treesFunded
    ? calculateCarbon({
        formTab: CalculatorTab.Stems,
        stems: treesFunded,
        emissions: 0,
        age: 50,
        trees: 25,
        shrubs: 75,
        includeOwnMeasurements: false,
        standTreeAge: 0,
        standTreeSurvival: 0,
        standTreeHeight: 0,
        standTreeDBH: 0,
        standShrubSurvival: 0,
        standShrubHeight: 0,
        standShrubRCD: 0
      })
    : null

  const treeCarbonValue = carbonValue?.co2Total || 0
  const treeCarbonValueFormatted = numeral(treeCarbonValue).format(
    treeCarbonValue < 100 ? '0,0.0' : '0,0'
  )

  const area = useMemo(() => {
    if (treesFunded) {
      const value = treesFunded * 2.89
      const formattedValue =
        value > 10_000
          ? numeral(value / 10_000).format('0,0.00')
          : numeral(value).format('0,0')
      const unit = value > 10_000 ? 'ha' : 'm²'
      return {
        value: formattedValue,
        unit
      }
    }
    return {
      value: '0',
      unit: 'm²'
    }
  }, [treesFunded])

  const tiles = useMemo<Array<JSX.Element>>(() => {
    const sortedDescriptions = tileDescriptions
      .filter(sd => sd.quantity <= treesFunded)
      .sort((a, b) => b.quantity - a.quantity)

    const matched = sortedDescriptions[0]

    if (!matched) return []
    const tileParams = [
      {
        description: (
          <>
            You&apos;ve funded {numeral(treesFunded).format('0,0')} native trees
            with{' '}
            <InternalLink href={listFundingPath}>your donations</InternalLink>{' '}
            since {accountJoinYear}.
          </>
        )
      },
      {
        title: `${treeCarbonValueFormatted} tCO₂e Expected to be removed.`,
        description: matched.carbon
      },
      {
        title: `${area.value} ${area.unit} land to be restored.`,
        description: matched.land
      },
      {
        title: `${projectsSupported} projects supported.`,
        description: (
          <>
            You&apos;re supporting credible{' '}
            <InternalLink href={supportedProjectsPath}>
              planting projects
            </InternalLink>{' '}
            backed by a team of ecologists.
          </>
        )
      }
    ]
    return tileParams.map((d, idx) => (
      <div
        key={`impact_tile_${idx}`}
        className='text-[14px] leading-5 font-medium text-charcoal'
      >
        {d.title ? (
          <>
            <span className='lg:hidden inline'>{d.title}</span>{' '}
          </>
        ) : null}
        <span>{d.description}</span>
      </div>
    ))
  }, [
    accountJoinYear,
    area.unit,
    area.value,
    projectsSupported,
    treeCarbonValueFormatted,
    treesFunded
  ])

  const summaryData = useMemo<Array<SummaryItem>>(
    () => [
      {
        stat: numeral(treesFunded).format('0,0'),
        text: 'Native trees funded',
        icon: FundedDark
      },
      {
        stat: treeCarbonValueFormatted,
        statUnitText: 'tCO₂e',
        text: 'Expected to be removed',
        icon: CarbonDark
      },
      {
        icon: AreaDark,
        stat: area.value,
        statUnitText: area.unit,
        text: 'Land to be restored'
      },
      {
        stat: numeral(projectsSupported).format('0,0'),
        text: (
          <>
            Projects supported
            {projectsSupported === 0 && treesFunded !== 0 ? (
              <Tooltip
                placement='bottom'
                overlay={
                  <div className='w-56 px-[5px] text-center'>
                    Your funding is still finding its roots. The number of
                    projects you’ve supported will show here once it’s
                    allocated.
                  </div>
                }
              >
                <IoInformationCircle
                  onClick={e => {
                    e.preventDefault()
                  }}
                  className='text-mostlyGreen w-5 h-5 inline ml-1 mb-1'
                />
              </Tooltip>
            ) : null}
          </>
        ),
        icon: SpadeDark
      }
    ],
    [
      area.unit,
      area.value,
      projectsSupported,
      treeCarbonValueFormatted,
      treesFunded
    ]
  )

  return (
    <div className='flex-1 flex gap-5 sm:gap-10 flex-col bg-white rounded-[10px] p-5 sm:p-10'>
      <Heading3 className='!m-0 !text-[18px] !leading-[26px] sm:!text-[28px] sm:!leading-[39px] !font-semibold'>
        Impact summary
      </Heading3>
      <div className='flex flex-col gap-5'>
        <div className='flex flex-col lg:flex-row justify-between gap-[15px] xl:gap-10'>
          {summaryData.map((item, idx) => {
            const Icon = () => (
              <item.icon className='flex-grow-0 flex-shrink-0 text-white w-10 h-10 xl:w-[50px] xl:h-[50px]' />
            )

            return (
              <div key={`summary_item_${idx}`} className='flex-1'>
                <div className='flex flex-row gap-[10px] items-center'>
                  <Icon />
                  <div className='text-mostlyGreen flex flex-col'>
                    <span className='flex flex-row items-end gap-1 font-bold text-[30px] leading-[30px] sm:text-[36px] sm:leading-[50px]'>
                      <span>{item.stat}</span>
                      {item.statUnitText ? (
                        <span className='text-[16px] leading-[40px]'>
                          {item.statUnitText}
                        </span>
                      ) : null}
                    </span>
                    <span className='font-medium text-[14px]'>{item.text}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        <SwipeTiles
          className='gap-[15px] xl:gap-7'
          tiles={tiles}
          backgroundColor='aquaSpring'
        />
        <ExternalLink
          className='self-end text-[14px] leading-5 font-medium !text-primary'
          href='https://help.treesthatcount.co.nz/en/articles/10566815-understanding-our-carbon-and-land-revegetation-calculations'
        >
          Learn more about how we calculate these.
        </ExternalLink>
      </div>
    </div>
  )
}
